/**
 * @file Components/Sections/InstagramGallery.jsx
 * @description Instagram-style photo grid showcasing travel moments.
 * Hover effects reveal location tags. Drives social engagement.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Heart, Camera } from 'lucide-react';

const photos = [
  { src: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=400&q=80', location: 'Taj Mahal, Agra', likes: 842 },
  { src: 'https://images.unsplash.com/photo-1506461883276-594a12b11cf3?w=400&q=80', location: 'Houseboat, Kerala', likes: 1203 },
  { src: 'https://images.unsplash.com/photo-1548013146-72479768bada?w=400&q=80', location: 'Varanasi Ghats', likes: 956 },
  { src: 'https://images.unsplash.com/photo-1564507592333-c60657eea523?w=400&q=80', location: 'Amber Fort, Jaipur', likes: 1547 },
  { src: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=400&q=80', location: 'Delhi Gate', likes: 723 },
  { src: 'https://images.unsplash.com/photo-1590077428593-a55bb07c4665?w=400&q=80', location: 'Munnar Tea Gardens', likes: 1089 },
  { src: 'https://images.unsplash.com/photo-1609766418204-94aae0ecfdfc?w=400&q=80', location: 'Pangong Lake, Ladakh', likes: 2104 },
  { src: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80', location: 'Beach, Goa', likes: 1876 },
];

const InstagramGallery = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Camera size={20} className="text-[#243185]" />
          <span className="text-sm font-bold text-[#243185] uppercase tracking-wider">Travel Moments</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Captured by Our Travellers
        </h2>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">
          Real moments from real adventures. Your next memory is waiting.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {photos.map((photo, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className={`group relative overflow-hidden rounded-xl cursor-pointer ${
              i === 0 || i === 5 ? 'row-span-2 h-[420px]' : 'h-[200px]'
            }`}
          >
            <img
              src={photo.src}
              alt={photo.location}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              loading="lazy"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center">
                <div className="flex items-center gap-1.5 text-white text-sm font-semibold mb-1">
                  <MapPin size={14} /> {photo.location}
                </div>
                <div className="flex items-center gap-1 text-white/70 text-xs">
                  <Heart size={12} className="fill-current text-red-400" /> {photo.likes.toLocaleString()}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default InstagramGallery;
